import {AttrsSchema, Component} from 'panel';

import {defineAndMakeJsxFactory} from 'common/util/jsx';
import {panel2react} from 'common/util/react';
import {SectionItem} from 'common/components/items-menu/types';
import {PositionParent} from 'common/components/drop-menu';
import {SelectedPropertyItem} from 'common/query-builder/query-block/properties-menu/types';

import {template} from './template';
import css from './index.inline.styl';

const CONTEXT_MENU_DEFAULTS = {
  breakdown: {label: `Expand by property`, icon: `segment`},
  hide: {label: `Hide event`, icon: `eye-hide`},
};

type ContextMenuState = {
  secondaryAction: `breakdown` | null;
  positionParent: PositionParent;
};

type ContextMenuAttrs = {
  'breakdown-options': {disabledItems?: string[]};
  'menu-items': SectionItem[];
  open: boolean;
  position: UnknownObj;
  'project-id': number;
  'workspace-id': number;
  'breakdown-event': string;
  'relative-positioning': boolean;
};

export class ContextMenu extends Component<ContextMenuState, ContextMenuAttrs> {
  private domEl: HTMLElement;

  static get attrsSchema(): AttrsSchema<ContextMenuAttrs> {
    return {
      ...super.attrsSchema,
      'breakdown-options': {type: `json`, default: {}},
      'menu-items': {type: `json`, default: []},
      open: `boolean`,
      position: {type: `json`, default: {}},
      'project-id': `number`,
      'workspace-id': `number`,
      'breakdown-event': {type: `string`, default: {}},
      'relative-positioning': `boolean`,
    };
  }

  get config() {
    return {
      template,
      useShadowDom: true,

      defaultState: {
        secondaryAction: null,
        positionParent: null,
      },
      helpers: {
        inserted: (vnode) => {
          if (!this.attr(`relative-positioning`)) {
            const anchor = (document.querySelector(`mp-browser-context-root`) as HTMLElement) || document.body;

            this.domEl = vnode.elm;
            anchor.style.position = `relative`;
            anchor.appendChild(this.domEl);
          }
        },
        getCss: () => css.toString(),
        handleBreakdownMenuSelect: (item: SelectedPropertyItem) => {
          this.dispatchEvent(
            new CustomEvent(`action`, {
              detail: {
                action: `breakdown`,
                value: item.item,
              },
            }),
          );
          this.reset();
        },
        handleContextMenuSelect: (ev) => {
          if (ev.detail.item.value === `breakdown`) {
            this.update({secondaryAction: `breakdown`});
          } else {
            this.dispatchEvent(new CustomEvent(`action`, {detail: {action: ev.detail.item.value}}));
            this.reset();
          }
        },
        handleDropMenuChange: (ev) => {
          if (ev.detail.state === `closed`) {
            this.dispatchEvent(new CustomEvent(`close`));
            this.reset();
          }
        },

        menuItems: () => this.attr(`menu-items`).map((item) => ({...CONTEXT_MENU_DEFAULTS[item.value], ...item})),
        breakdownDisabledItemsMap: () =>
          (this.attr(`breakdown-options`).disabledItems || []).reduce((map, item) => ({...map, [item]: true}), {}),
      },
    };
  }

  disconnectedCallback() {
    if (this.domEl) {
      this.domEl.remove();
    }
  }

  reset() {
    this.update(this.config.defaultState);
  }

  set positionParent(positionParent: PositionParent) {
    this.update({positionParent});
  }
}

export const ContextMenuEl = defineAndMakeJsxFactory(`mp-context-menu`, ContextMenu);
export const ContextMenuReact = panel2react(`mp-context-menu`, ContextMenu);
